import { SecureLoader } from "@ews-admin/global-design-system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { fetchLicenceRequest, selectLicenceState } from "../../store/reducers/licenceSlice";
import { resetLoader } from "../../store/reducers/loaderSlice";
import { selectFeatureFlag, selectSessionAuthState } from "../../store/reducers/sessionSlice";
import { RootState } from "../../store/store";
import { FirstPaymentBanner } from "./FirstPaymentBanner";
import { NoLicenceBanner } from "./NoLicenceBanner";
import { PaymentDueBanner } from "./PaymentDueBanner";
import { SuspendedLicenceBanner } from "./SuspendedLicenceBanner";
import { Sidebar } from "./Sidebar";
import { TopNavigation } from "./TopNavigation";

export const DashboardLayout: React.FC = () => {
  const dispatch = useDispatch();
  const { session, sessionStatus, loading: sessionLoading } = useSelector(selectSessionAuthState);
  const { licence, loading: licenceLoading, errorCode: licenceErrorCode } = useSelector(selectLicenceState);
  const isManagerLicenceEnabled = useSelector((state: RootState) =>
    selectFeatureFlag(state, "MANAGER_LICENCE")
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const userRole = session?.user?.roleName?.toLowerCase();
  const isManager = userRole === "manager";
  const isSecretary = userRole === "secretary";
  const facilityId =
    session?.metadata?.manager?.healthcareFacility?.id ??
    session?.metadata?.secretary?.healthcareFacility?.id;

  // Scroll to top when route changes
  useScrollToTop("auto");

  // Reset loader on component mount (page load/reload) to prevent stuck loader state
  useEffect(() => {
    // Reset loader on mount to clear any stuck state from previous session
    // Use a small delay to ensure it runs after any initial requests
    const timer = setTimeout(() => {
      dispatch(resetLoader());
    }, 100);
    return () => clearTimeout(timer);
  }, [dispatch]);

  // Fetch licence once when facilityId is available — MANAGER only.
  // The BFF blocks SECRETARY from this endpoint (no access to billing/licence data),
  // and any 403 there force-redirects to login via the axios interceptor.
  useEffect(() => {
    if (!isManager || !facilityId) return;
    dispatch(fetchLicenceRequest({ facilityId: Number(facilityId) }));
  }, [dispatch, isManager, facilityId]);

  // Handle responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(false); // Close sidebar on desktop
      }
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const closeSidebar = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const secureLoader = (
    <SecureLoader
      label={t("common.loading")}
      description={t("common.secureLoading")}
    />
  );

  // Wait for session to be validated before rendering anything
  if (sessionLoading || sessionStatus === null) {
    return secureLoader;
  }

  // For managers: wait for licence to be resolved before rendering the layout
  if (isManager && facilityId && (licenceLoading || (!licence && !licenceErrorCode))) {
    return secureLoader;
  }

  const hasNoLicence = isManager && licenceErrorCode === "LICENCE_NOT_FOUND";
  // MED-1220: a TRIAL licence has also never been paid (last_payment_date is
  // null), but unlike an admin-onboarded ACTIVE licence there is nothing to
  // pay yet — billing only starts once the trial ends. Must not fall into
  // "first payment" (nor, by the same logic, PaymentDueBanner — though that
  // one already self-guards on a null payment_due_date).
  const isTrial = isManager && licence?.status === "TRIAL";
  // Admin-onboarded managers get an ACTIVE licence they have never paid for
  // (last_payment_date is only set on the first successful payment). They are
  // first-time users, not overdue customers — show a welcoming "make your first
  // payment" banner instead of the "risk of suspension" one.
  const isFirstTimeSetup =
    isManager && !!licence && !licence.lastPaymentDate && !isTrial;
  // A licence suspended by the administration (PAUSED) is not a payment
  // matter — paying does not lift it — so it takes priority over the first
  // payment / payment-due banners, whatever lastPaymentDate says.
  const isSuspended = isManager && licence?.status === "PAUSED";
  const hasNoFacility = (isManager || isSecretary) && !facilityId;
  // The licence page is where these banners send the user (their CTA target),
  // so showing them on top of that page is redundant — hide them there.
  const onLicencePage = location.pathname.startsWith("/manager/licence");
  // With the manager licence feature switched off, no licence banner is shown at all.
  const showLicenceBanners = isManager && isManagerLicenceEnabled;

  return (
    <div className="flex h-screen bg-neutral-50">
      {/* Mobile Overlay */}
      {!hasNoFacility && sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar — hidden when manager has no facility assigned */}
      {!hasNoFacility && (
        <div
          className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <Sidebar onClose={closeSidebar} isMobile={isMobile} />
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex flex-col flex-1 overflow-hidden lg:ml-0">
        {/* Header */}
        <TopNavigation onMenuClick={!hasNoFacility ? () => setSidebarOpen(!sidebarOpen) : undefined} />

        {/* Suspended licence banner — shown on every page, the licence page
            included, since its CTA points to support rather than there. */}
        {showLicenceBanners && isSuspended && <SuspendedLicenceBanner />}

        {/* No Licence / First-time onboarding / Payment Due Banner —
            hidden on the licence page itself (the banners' CTA target). */}
        {showLicenceBanners && !onLicencePage && !isSuspended &&
          (hasNoLicence ? (
            <NoLicenceBanner />
          ) : isFirstTimeSetup ? (
            <FirstPaymentBanner />
          ) : (
            <PaymentDueBanner />
          ))}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
